import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../../../redux/store";
import {useSnack} from "../../../utils/useSnack";
import {Company, CompanyFile} from "../../../types/company";
import {FormikProps} from "formik";
import {NewCommonForm} from "./components/Common/formik";
import {CommonFields} from "./components/Common";
import {Button, Chip, Divider, Grid, LinearProgress, Typography} from "@mui/material";
import Container from "../../../common/Container";
import {HashLotItem, Lot, LotCompanyFile, LotItem} from "../../../types/lot";
import {ArchivedFilesModal} from "../../../common/FilesModals/Archived";
import {NewFileModal} from "../../../common/FilesModals/New";
import {PlanItemModal} from "../../../common/PlanItemModal";
import {AuctionType, PlanItemState, LotState, ProductType} from "../../../utils/enums";
import {companyService} from "../../../api/services/company";
import {publicService} from "../../../api/services/public";
import {AxiosError} from "axios";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import {ActionColumn, Column, columnStyle, DateColumn, DateTimeColumn, GrossColumn} from "../../../common/Columns";
import {PlanItemList} from "../../../types/plan";
import {DataGrid} from "@mui/x-data-grid";
import {lotService} from "../../../api/services/lot.service";
import {formValidation} from "../../../utils/formValidation";
import {Response} from "../../../types/api";
import {SET_LOT} from "../../../redux/reducers/lot";
import {useConfirmation} from "../../../context/ConfirmationModalProvider";
import {hash} from "../../../utils/hash";
import {useDictionary} from "../../../utils/useDictionary";

export default function NewLot({
                                   lotEditing, setLotEditing
                               }: {
    lotEditing?: boolean;
    setLotEditing?: React.Dispatch<React.SetStateAction<boolean>>
}) {

    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const state = useSelector((state: State) => state);
    const {snack} = useSnack();
    const dispatch = useDispatch();
    const {confirm} = useConfirmation()

    const company: Company = state.company.company;
    const commonFormikRef = React.useRef<FormikProps<NewCommonForm>>(null);
    const [loading, setLoading] = useState(lotEditing);
    const lot: Lot | null = state.lot.lot;

    const [attachments, setAttachments] = useState<LotCompanyFile[]>([]);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const [isArchivedOpen, setIsArchivedOpen] = useState(false);
    const onOpenArchived = () => setIsArchivedOpen(true);
    const onCloseArchived = () => setIsArchivedOpen(false);
    const [isNewOpen, setIsNewOpen] = useState(false);

    const [isPlanItemOpen, setIsPlanItemOpen] = useState(false);
    const onOpenPlanItem = () => setIsPlanItemOpen(true);
    const onClosePlanItem = () => setIsPlanItemOpen(false);

    const [lotItems, setLotItems] = useState<LotItem[]>([]);

    const pathArray = location.pathname.split('/');

    const lotId = lotEditing ? +pathArray[pathArray.length - 1] : -1

    const getLot = async () => {
        if (lotId === -1) {
            return
        }
        try {
            const response = await lotService.getLot(company!.id, lotId)
            if (!!response && !!response.data && response.status === "success") {
                dispatch({type: SET_LOT, payload: response.data})
                setLotItems(response.data.lotItems || [])
                setAttachments(response.data.attachments || [])
            }
        } catch (error) {
            snack(t("snack.error.default"), "error")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (lotEditing) {
            getLot();
        }
    }, [lotEditing, lotId, company.id, t, navigate]);

    const editingInitialState = lotEditing && lot ? {
        common: {
            companyId: lot.companyId,
            auctionType: {
                value: lot.auctionType,
                label: t(`auction_type.${lot.auctionType}`)
            },
            deliveryType: lot.deliveryType,
            deliveryDate: lot.deliveryDate,
            auctionDate: lot.auctionDate,
            year: lot.year,
            productType: {
                value: lot.productType,
                label: t(`product_type.${lot.productType}`)
            },
        }
    } : undefined;
    const disabled = !!lot?.state && lot?.state !== LotState.draft

    const formikRefs = [
        commonFormikRef
    ]

    const validateAll = () => formikRefs.forEach(ref => formValidation(ref));
    const allIsValid = () => formikRefs
        .map(ref => ref.current?.isValid)
        .every(item => item);

    const handleSubmit = async () => {
        if (!company) {
            return
        }

        const allRefsDefined = (
            commonFormikRef.current &&
            true
        );

        if (attachments?.length === 0) {
            snack(t("snack.error.document_empty"), "error")
            return;
        }

        if (lotItems.length === 0) {
            snack(t("snack.error.plan_item_empty"), "error")
            return;
        }

        validateAll();

        if (allRefsDefined && allIsValid()) {
            const common = commonFormikRef.current?.values;

            const data = {
                companyId: common.companyId!,
                year: common.year!,
                auctionType: common!.auctionType.value! as AuctionType,
                intercomId: common.deliveryType!.id!,
                deliveryDate: common.deliveryDate!,
                auctionDate: common.auctionDate!,
                attachments: attachments.map((file: CompanyFile) => file.fileId),
                lotItems: lotItems.map((item: LotItem) => item.planItemId!),
                productType: common!.productType.value! as ProductType,
            };

            try {
                let response: Response<Lot>;
                if (lotEditing) {
                    response = await lotService.editLot(company.id, lotId, data)
                } else {
                    response = await lotService.createLot(company.id, data)
                }

                if (response && response.data && response.status === "success") {
                    snack(
                        lotEditing
                            ? t("snack.success.lot_edited")
                            : t("snack.success.lot_created"),
                        "success"
                    );
                    navigate(`/account-lots`)
                    // if (setLotEditing) setLotEditing(true);
                } else {
                    snack(t("snack.error.default"), "error")
                }
            } catch (error) {
                const err = error as AxiosError;
                console.log(err.response)
                snack(t("snack.error.default"), "error")
            }
        }
    }

    const downloadFile = async (file: CompanyFile) => {
        if (!company) {
            return
        }

        try {
            setDownloadingId(file.fileId);
            const fileTokenResponse = await companyService.getFileToken(company.id, file.fileId);
            if (fileTokenResponse.status === "success" && fileTokenResponse.data) {
                publicService.downloadFile(fileTokenResponse.data.id, file.name);
            } else {
                snack(t("snack.error.file_download"), "error")
            }
        } catch (error) {
            const err = error as AxiosError;
            snack(t("snack.error.default"), "error")
        } finally {
            setDownloadingId(null)
        }
    }

    const pickFile = async (file: LotCompanyFile) => {
        if (attachments.filter(item => item.fileId === file.fileId).length === 0) {
            if (!lotEditing) {
                setAttachments(prev => [...prev, file]);
                snack(t("snack.success.document_attached"), "success")
            } else {
                setLoading(true)
                try {
                    const response = await lotService.addLotFiles(company.id, lotId, [file.fileId])
                    if (response.status === "success" && response.data) {
                        setAttachments(prev => [...prev, file]);
                        snack(t("snack.success.document_attached"), "success")
                    }
                } catch (error) {
                    snack(t("snack.error.default"), "error")
                } finally {
                    setLoading(false)
                }
            }
        } else {
            snack(t("snack.info.document_already_attached"), "info")
        }
    };

    const onCloseNew = () => setIsNewOpen(false)
    const onOpenNew = () => setIsNewOpen(true)

    const getItemHashKey = useMemo(() => {
        return (item: LotItem | PlanItemList) => {
            const hashItem: HashLotItem = {
                productCode: item.productCode,
                productName: item.productName,
                productBrief: item.productBrief,
                amount: item.amount,
                gross: item.gross
            };

            return hash.computeObjectHash(hashItem);
        };
    }, []);

    const addNewLotItems = async (items: number[]) => {
        let response = await lotService.addLotItems(company.id, lotId, items)
        if (response.status === "success" && response.data) {
            return response.data
        }
        return []
    }

    const addNewLotFiles = async (files: number[]) => {
        let response = await lotService.addLotFiles(company.id, lotId, files)
        if (response.status === "success" && response.data) {
            return response.data
        }
        return []
    }

    const pickPlanItems = async (planItems: PlanItemList[]) => {
        let newLotItemIds: number[] = [];
        const itemsMap = new Map<string, LotItem>()
        lotItems.forEach((prevItem: LotItem) => {

            // Добавляем предыдущие значения

            const hashKey = getItemHashKey(prevItem)
            itemsMap.set(hashKey, prevItem)
        });

        // Добавляем новые значения, перезаписывая при совпадении id
        planItems.forEach((item: PlanItemList, index: number) => {
            const hashKey = getItemHashKey(item);
            if (itemsMap.has(hashKey)) {
                snack(t("snack.error.plan_item_duplicate"), "error")
            } else if (!lotEditing) {
                const newItem: LotItem = JSON.parse(JSON.stringify(item))
                newItem.id = index
                newItem.productName = item.productName
                newItem.productBrief = item.productBrief
                newItem.productCode = item.productCode
                newItem.amount = item.amount
                newItem.gross = item.gross
                newItem.unitName = item.unitName
                newItem.planItemId = item.id
                itemsMap.set(hashKey, newItem)
            } else {
                newLotItemIds.push(item.id)
            }
        });

        if (!lotEditing) {
            // @ts-ignore
            setLotItems(Array.from(itemsMap.values()))
        } else if (newLotItemIds.length > 0) {
            try {
                setLoading(true);
                let newLotItems: LotItem[] = await addNewLotItems(newLotItemIds)
                setLotItems(prev => [...prev, ...newLotItems])
            } catch (error) {
                snack(t("snack.error.default"), "error")
            } finally {
                setLoading(false);
            }
        }
        setIsPlanItemOpen(false)
    }

    const SUCCESS_STATUS = "success";

    const handleDeleFile = async (lotFile: LotCompanyFile) => {
        if (!company) {
            snack(t("snack.error.no_company"), "error");
            return;
        }
        if (!lotFile.id) {
            setAttachments((prevItems) => prevItems.filter((item) => item.fileId !== lotFile.fileId));

        } else {
            try {
                setLoading(true);

                // const userConfirmed = await showDeleteConfirmation();
                // if (!userConfirmed) return;
                const response = await lotService.deleteLotFile(company.id, lotFile.id, lotId);

                if (response.status === SUCCESS_STATUS) {
                    snack(t("snack.success.file_deleted"), "success");
                    setAttachments((prevItems) => prevItems.filter((item) => item.id !== lotFile.id));
                } else {
                    snack(t("snack.error.default"), "error");
                }
            } catch (error) {
                const err = error as AxiosError;
                snack(err.message || t("snack.error.default"), "error");
            } finally {
                setLoading(false);
            }
        }
    }

    const handleDeleteLotItem = async (lotItem: LotItem) => {
        if (!company) {
            snack(t("snack.error.no_company"), "error");
            return;
        }

        if (!lotItem.id) {
            const rowIndex = lotItems.findIndex(item => item === lotItem);
            setLotItems(lotItems.filter((_: LotItem, index: number) => index !== rowIndex));

        } else if (await showDeleteConfirmation()) {
            try {
                setLoading(true);

                const response = await lotService.deleteLotItem(company.id, lotItem.id, lotId);

                if (response.status === SUCCESS_STATUS) {
                    snack(t("snack.success.plan_item_deleted"), "success");
                    setLotItems((prevItems) => prevItems.filter((item) => item.id !== lotItem.id));

                } else {
                    snack(t("snack.error.plan_item_delete_failure"), "error");
                }
            } catch (error) {
                const err = error as AxiosError;
                snack(err.message || t("snack.error.default"), "error");
            } finally {
                setLoading(false);
            }
        }
    };

    const showDeleteConfirmation = async (): Promise<boolean> => {
        return confirm({
            title: t('cabinet.plan.confirm.remove_plan_item.title'),
            message: t('cabinet.plan.confirm.remove_plan_item.body'),
        });
    };


    const actions = [
        {
            label: "action.navigate",
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                navigate(`/account-plan/${row.planId}`);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return true;
            }
        },
        {
            label: "action.remove",
            callback: async (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                await handleDeleteLotItem(row);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return !!lot && lot.state === LotState.draft;
            }
        }]

    function onClose() {
        navigate("/account-lots", {replace: true});
    }

    return (
        <Container>
            <Grid container rowSpacing={3}>
                <CommonFields formikRef={commonFormikRef}
                              editingInitialState={editingInitialState?.common}
                              company={company} lotEditing={!!lotEditing} disabled={disabled}
                />

                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>


            </Grid>
            <Grid container columnSpacing={3} rowSpacing={1}>
                <ArchivedFilesModal
                    isOpen={isArchivedOpen} onClose={onCloseArchived}
                    pickFile={(file: CompanyFile) => pickFile(file as LotCompanyFile)} downloadFile={downloadFile}
                    downloadingId={downloadingId} onOpenNew={onOpenNew}
                />
                <NewFileModal
                    isOpen={isNewOpen} onClose={onCloseNew}
                    pickFile={(file: CompanyFile) => pickFile(file as LotCompanyFile)}
                />

                <PlanItemModal isOpen={isPlanItemOpen} onClose={onClosePlanItem} company={company}
                               financialYear={new Date().getFullYear()}
                               auctionType={editingInitialState?.common?.auctionType?.value || commonFormikRef?.current?.values?.auctionType?.value as AuctionType || AuctionType.tender}
                               productType={editingInitialState?.common?.productType?.value || commonFormikRef?.current?.values?.productType?.value as ProductType || ProductType.product}
                               planItemStates={[PlanItemState.approved]} pickPlanItems={pickPlanItems}/>

                <Grid item xs={12} sx={{mb: 1}}>
                    <Typography variant="h6">
                        {t("cabinet.new_lot.sections.attached_files")}
                    </Typography>
                </Grid>


                <Grid item>
                    <Grid item container columnSpacing={3} rowSpacing={1}>
                        <Grid item>
                            <Button disabled={disabled} color={"primary"} variant="contained" size="small"
                                    onClick={onOpenArchived}>
                                {t("auction.participate.add_archived")}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button disabled={disabled} color={"primary"} variant="contained" size="small"
                                    onClick={onOpenNew}>
                                {t("auction.participate.add_new")}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item container columnSpacing={2} rowSpacing={1}>
                    {attachments.map(file => {
                        return (
                            <Grid item key={file.id}>
                                <Typography fontSize={12} textAlign={"center"} mt={1} mb={.5}>
                                    {t(`file_category.${file.category}`)}
                                </Typography>

                                <Chip key={file.id}
                                      label={file.title || file.name}
                                      onClick={() => downloadFile(file)}
                                      disabled={downloadingId === file.id}
                                      onDelete={() => {
                                          if (disabled) {
                                              return
                                          }
                                          handleDeleFile(file)
                                      }}
                                />

                                {downloadingId === file.id &&
                                    <LinearProgress color="primary"/>
                                }
                            </Grid>
                        )
                    })}
                </Grid>

                <Divider sx={{mt: 3, mb: 2, ml: "auto", width: '98%'}}/>

                <Grid item xs={12} sx={{mb: 1}}>
                    <Typography variant="h6">
                        {t("cabinet.new_lot.sections.plan_items")}
                    </Typography>
                </Grid>

                <Grid item>
                    <Grid item container columnSpacing={3} rowSpacing={1}>
                        <Grid item>
                            <Button disabled={disabled} color={"primary"} variant="contained" size="small"
                                    onClick={onOpenPlanItem}>
                                {t("cabinet.new_lot.add_pan_items")}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item container sx={{mt: 2}}>
                    <DataGrid
                        getRowId={(row: LotItem) => row.id || ""}
                        components={{
                            NoRowsOverlay: () => <NoRowsOverlay/>,
                        }}

                        sx={{
                            height: 500,
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "pre-wrap"
                            },
                        }}
                        columnBuffer={4}
                        rows={lotItems.length
                            ? lotItems
                            : []
                        }
                        columns={[
                            ActionColumn(100, columnStyle, "cabinet.plans", actions),
                            Column("planItemId", 50, columnStyle, undefined, "cabinet.plan"),
                            Column("productName", 350, columnStyle, undefined, "cabinet.plan"),
                            Column("productCode", 100, columnStyle, undefined, "cabinet.plan"),
                            Column("productBrief", 350, columnStyle, undefined, "cabinet.plan"),
                            Column("amount", 100, columnStyle, undefined, "cabinet.plan"),
                            Column("unitName", 100, columnStyle, undefined, "cabinet.plan"),
                            DateTimeColumn("createdAt", 200, columnStyle, "cabinet.plan"),
                            GrossColumn(150, columnStyle, "cabinet.plan")
                        ]}
                        loading={loading}
                        density="comfortable" style={{whiteSpace: "pre-wrap"}}
                    />
                </Grid>
            </Grid>

            <Divider sx={{mt: 6, mb: 2, width: "100%"}}/>

            <Grid item container spacing={2} justifyContent="center" sx={{pt: 2}}>
                <Grid item xs="auto">
                    <Button fullWidth variant="contained" size="large"
                            onClick={onClose} color="secondary"
                    >
                        {disabled ? t("common.return_back") : t("plan_item.cancel")}
                    </Button>
                </Grid>
                <Grid item xs="auto">
                    <Button disabled={disabled} fullWidth variant="contained" color="primary"
                            onClick={handleSubmit} size="large">
                        {lotEditing
                            ? t("plan_item.save_changes")
                            : t("cabinet.lots.create-lot")
                        }
                    </Button>
                </Grid>
            </Grid>
        </Container>
    )
}